import { useEffect, useRef } from 'react';
import { RobotConnEventType } from '../../stores';
import { useRobotStateStore } from '../../stores/useRobotStateStore';
import { RobotDevice } from '../../types';
import {
  addConnectionListeners,
  removeConnectionListeners,
} from '../functions';
import { RobotConnectionListeners } from '../types';

export type RobotConnectionListenerProps = {
  robotId: string;
  robotConnListeners: RobotConnectionListeners;
};

/**
 * Get list of existing robot devices from store.
 * This is not part of react, and is not regularly tracked
 *
 * @param robotId id of the robot
 * @returns object of robotId: robotDevice
 */
const getExistingRobotDevices = (robotId: string) => {
  const devices = useRobotStateStore?.getState()?.robotDevices;

  if (devices === undefined) {
    return {};
  }

  if (robotId === '*') {
    return devices;
  } else if (devices[robotId] !== undefined) {
    return { robotId: devices[robotId] };
  } else {
    return {};
  }
};

/**
 * Hook to add a listener to a robot connection.
 *
 * @param props.robotId ID to attach listeners to.
 * If robotId === '*', then the listeners are added to all connections.
 * Notably, while this hooks is active, all matching connections created in the
 * past, present and future will have the listeners attached to them
 *
 * @param props.robotConnListeners Object map containing all the robot connection listeners
 */
export const useRobotConnectionListener = (
  props: RobotConnectionListenerProps
) => {
  const { robotId, robotConnListeners } = props;

  const addEventListener = useRobotStateStore(
    (state) => state.addRobotConnEventListener
  );

  const robotDevices = useRef<{ [id: string]: RobotDevice }>(
    getExistingRobotDevices(robotId)
  );

  /**
   * Add event listeners to the robot connection manager, such that whenever we add a socket,
   * we add the event listeners necessary.
   */
  useEffect(() => {
    const cleanupFunc = addEventListener({
      event: RobotConnEventType.NewSocket,
      callback: (device: RobotDevice) => {
        if (robotId === '*' || device.id === robotId) {
          robotDevices.current[device.id] = device;
          addConnectionListeners(device, robotConnListeners);
        }
      },
    });

    return () => {
      cleanupFunc();
    };
  }, [robotId, robotConnListeners, addEventListener]);

  /**
   * If the listeners attached to the socket changes, then we want to change the listeners
   * that were previously added to the sockets.
   * To do this, we remove the listeners attached to each socket, and then we re-add the latest version.
   *
   * Note: There is a very very unlikely edge case that a message may be received between
   * the time the listener was removed and another one was added.
   */
  useEffect(() => {
    Object.values(robotDevices.current).forEach((conn) => {
      addConnectionListeners(conn, robotConnListeners);
    });

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      Object.values(robotDevices.current).forEach((conn) => {
        removeConnectionListeners(conn, robotConnListeners);
      });
    };
  }, [robotConnListeners]);
};
